import { keepPreviousData } from '@tanstack/react-query';
import { bunja } from 'bunja';
import { atom } from 'jotai';

import { getTradingCompetitionRankingsApi } from '@flipster/apis/v2/trading-competition';
import { RankingType, isRankingType } from '@flipster/apis/v2/trading-competition/types';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { authBunja } from '../../../../shared/auth/state';
import { CompetitionIdScope, competitionTabsBunja, createAxiosQueryAtoms } from '../../state';

export const competitionRankingsBunja = bunja(() => {
  const { competitionRankingsAtoms, competitionMyRankingsAtoms, offsetAtom, limitAtom } =
    bunja.use(competitionRankingsApiBunja);

  const { dataAtom: rankingsDataAtom, errorAtom: rankingsErrorAtom } = competitionRankingsAtoms;

  const rankingsAtom = atom((get) => get(rankingsDataAtom)?.data);
  const rankingsPaginationAtom = atom((get) => {
    const pagination = get(rankingsDataAtom)?.pagination;
    const currentOffset = get(offsetAtom);
    const currentLimit = get(limitAtom);
    return {
      total: pagination?.total ?? 0,
      offset: pagination?.offset ?? currentOffset,
      limit: currentLimit,
    };
  });
  const lastUpdatedAtAtom = atom((get) => get(rankingsDataAtom)?.lastUpdatedTime);

  // handlers
  const onOffsetChangeAtom = atom(null, (_get, set, nextOffset: number) => {
    set(offsetAtom, Math.max(0, nextOffset));
  });

  const { dataAtom: myRankingsDataAtom, errorAtom: myRankingsErrorAtom } = competitionMyRankingsAtoms;

  const myRankingsAtom = atom((get) => get(myRankingsDataAtom)?.data);
  const myRankingsPaginationAtom = atom((get) => {
    const pagination = get(myRankingsDataAtom)?.pagination;
    const currentOffset = get(offsetAtom);
    const currentLimit = get(limitAtom);

    return {
      total: pagination?.total ?? 0,
      offset: pagination?.offset ?? currentOffset,
      limit: currentLimit,
      page: pagination?.page ?? 0,
    };
  });

  return {
    // data atoms
    rankingsAtom,
    rankingsPaginationAtom,
    lastUpdatedAtAtom,
    myRankingsAtom,
    myRankingsPaginationAtom,
    // handlers
    onOffsetChangeAtom,
    // error atoms
    rankingsErrorAtom,
    myRankingsErrorAtom,
  };
});
competitionRankingsBunja.debugLabel = 'competitionRankingsBunja';

export const competitionRankingsApiBunja = bunja(() => {
  const id = bunja.use(CompetitionIdScope);
  const { selectedEventTabValueAtom } = bunja.use(competitionTabsBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const store = bunja.use(JotaiStoreScope);
  // pagination state
  const offsetAtom = atom(0);
  const limitAtom = atom(10);

  const competitionRankingsQueryKeyAtom = atom((get) => {
    const selectedEventTabValue = get(selectedEventTabValueAtom);
    const offset = get(offsetAtom);
    const limit = get(limitAtom);
    if (!isRankingType(selectedEventTabValue)) return ['getCompetitionRankings', id, offset, limit];
    return ['getCompetitionRankings', id, selectedEventTabValue, offset, limit];
  });

  const competitionRankingsAtoms = createAxiosQueryAtoms((get) => ({
    queryKey: get(competitionRankingsQueryKeyAtom),
    queryFn: () =>
      getTradingCompetitionRankingsApi({
        competitionUrl: id,
        rankingMethod: get(selectedEventTabValueAtom) as RankingType,
        myRanking: false,
        offset: get(offsetAtom),
        limit: get(limitAtom),
      }),
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5,
    enabled: isRankingType(get(selectedEventTabValueAtom)),
    placeholderData: keepPreviousData,
  }));

  const competitionMyRankingsAtoms = createAxiosQueryAtoms((get) => ({
    queryKey: [...get(competitionRankingsQueryKeyAtom), 'myRanking'],
    queryFn: () =>
      getTradingCompetitionRankingsApi({
        competitionUrl: id,
        rankingMethod: get(selectedEventTabValueAtom) as RankingType,
        myRanking: true,
      }),
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5,
    enabled: get(isCookieLoggedInAtom) && isRankingType(get(selectedEventTabValueAtom)),
  }));

  const initializePagination = () => {
    store.set(offsetAtom, 0);
    store.set(limitAtom, 10);
  };

  return {
    competitionRankingsAtoms,
    competitionMyRankingsAtoms,
    // query keys
    competitionRankingsQueryKeyAtom,
    // pagination atoms
    offsetAtom,
    limitAtom,
    initializePagination,
  };
});
competitionRankingsApiBunja.debugLabel = 'competitionRankingsApiBunja';
